import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/** Extract a human-readable message from Backstage error responses.
 *  Handles both `{ error: "string" }` and `{ error: { message: "string" } }`.
 */
function extractErrorMessage(body: any, fallback: string): string {
  const err = body?.error;
  if (typeof err === 'string') return err;
  if (err && typeof err.message === 'string') return err.message;
  return fallback;
}

export interface ExtractionQuestion {
  id: string;
  question: string;
  context?: string;
  options?: string[];
}

export interface ExtractedEntity {
  kind: 'system' | 'api' | 'component' | 'resource' | 'screen' | 'story';
  name: string;
  description?: string;
  type?: string;
  lifecycle?: string;
  platform?: string;
  route?: string;
  technology?: string;
}

export interface ExtractedRelationship {
  sourceIndex: number;
  targetIndex: number;
}

export type ExtractionResponse =
  | {
      status: 'clarifying';
      questions: ExtractionQuestion[];
      partialData?: {
        entities: ExtractedEntity[];
        relationships: ExtractedRelationship[];
      };
    }
  | {
      status: 'complete';
      data: {
        entities: ExtractedEntity[];
        relationships: ExtractedRelationship[];
      };
    };

export interface HealthResponse {
  status: 'ok' | 'degraded';
  ai: {
    configured: boolean;
    provider: string;
    model: string;
  };
  source?: 'vault' | 'config' | 'none';
  message?: string;
}

export interface AiSettingsResponse {
  configured: boolean;
  provider?: string;
  baseUrl?: string;
  model?: string;
  apiKey?: string;
  email?: string;
  source?: string;
}

export interface AiSettingsPayload {
  provider:
    | 'openai'
    | 'anthropic'
    | 'gemini'
    | 'ollama'
    | 'github-copilot'
    | 'github-models'
    | 'kimi'
    | 'custom';
  baseUrl: string;
  apiKey: string;
  model: string;
  email?: string;
}

export interface AiCallRecord {
  timestamp: string;
  provider: string;
  model: string;
  operation: string;
  durationMs: number;
  success: boolean;
  error?: string;
  inputTokens?: number;
  outputTokens?: number;
  estimatedCostUsd?: number;
}

export interface AiUsageSummary {
  totalCalls: number;
  successCount: number;
  failureCount: number;
  totalDurationMs: number;
  avgDurationMs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalEstimatedCostUsd: number;
  byModel: Record<
    string,
    {
      calls: number;
      totalDurationMs: number;
      avgDurationMs: number;
      inputTokens: number;
      outputTokens: number;
      estimatedCostUsd: number;
    }
  >;
  byOperation: Record<
    string,
    { calls: number; totalDurationMs: number; avgDurationMs: number }
  >;
  calls: AiCallRecord[];
}

export interface UploadDocumentResponse {
  documentId: string;
  fileName: string;
  textContent: string;
  s3Key: string;
  bucket: string;
  truncated: boolean;
  transcriptionStatus?: 'processing';
}

export interface TranscriptionStatusResponse {
  s3Key: string;
  status: 'processing' | 'completed' | 'failed' | 'unknown';
  error?: string;
  transcriptFile?: string;
}

export type GitProvider =
  | 'github'
  | 'gitea'
  | 'gitlab'
  | 'bitbucket'
  | 'azure-devops'
  | 'generic';

export interface ValidateRepoResponse {
  valid: boolean;
  accessible: boolean;
  provider?: GitProvider;
  owner?: string;
  repo?: string;
  cloneUrl?: string;
  error?: string;
}

export interface RepoScanResult {
  url: string;
  success: boolean;
  scan?: {
    repoUrl: string;
    languages: Record<string, number>;
    frameworks: string[];
    summary: string;
    fileTree: string;
    configSnippets: Array<{ path: string; content: string }>;
  };
  provider?: string;
  owner?: string;
  repo?: string;
  error?: string;
}

export interface ScanReposResponse {
  results: RepoScanResult[];
  combinedContext: string;
}

export interface PrResultItem {
  url: string;
  success: boolean;
  pr?: {
    url: string;
    number: number;
    title: string;
    repo: string;
  };
  error?: string;
}

export interface CreateRepoPrsResponse {
  results: PrResultItem[];
}

export interface RegisterEntityResult {
  name: string;
  kind: string;
  status: 'created' | 'error';
  error?: string;
}

export interface RegisterEntitiesResponse {
  success: boolean;
  message: string;
  results: RegisterEntityResult[];
}

export type ProjectMode = 'simple' | 'advanced' | 'import';
export type ProjectStatus = 'draft' | 'submitted' | 'registered';

export interface ProjectSummary {
  id: string;
  name: string;
  description: string | null;
  owner: string;
  mode: ProjectMode;
  status: ProjectStatus;
  currentStep: number;
  automationProjectId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetail extends ProjectSummary {
  stateJson: string | null;
}

export interface CreateProjectPayload {
  name: string;
  description?: string;
  owner?: string;
  mode: ProjectMode;
  stateJson?: string;
}

export interface UpdateProjectPayload {
  name?: string;
  description?: string;
  owner?: string;
  currentStep?: number;
  stateJson?: string;
}

export interface GiteaOrg {
  id: number;
  name: string;
  full_name: string;
  avatar_url: string;
  description: string;
}

export interface GiteaRef {
  name: string;
  type: 'branch' | 'tag';
  isDefault?: boolean;
}

export interface GiteaRefsResponse {
  defaultBranch: string;
  branches: Array<{ name: string; commit: { id: string; message: string } }>;
  tags: Array<{ name: string; id: string; commit: { sha: string } }>;
}

// Manual task phase types for the Tasks Board
export type ManualPhaseKey =
  | 'backlog'
  | 'todo'
  | 'inProgress'
  | 'doneDev'
  | 'inTesting'
  | 'reject'
  | 'done'
  | 'deployed';

export interface ManualTaskRecord {
  taskId: string;
  phase: ManualPhaseKey;
  agenticId: string | null;
  batchAgenticId: string | null;
}

export interface ManualTaskPhasesResponse {
  phases: Record<string, ManualPhaseKey>;
  records?: ManualTaskRecord[];
}

export interface GeneratedStory {
  name: string;
  title: string;
  category?: string;
  description: string;
  objective: string;
  businessFlow?: string;
  difficulty?: 'trivial' | 'moderate' | 'complex';
  storyPoints?: number;
  actors: Array<{ name: string; description?: string }>;
  nodes?: string[];
  acceptanceCriteria: Array<{
    scenario: string;
    id?: string;
    type?: string;
    actor?: string[];
    given: string | string[];
    when: string | string[];
    and_when?: string | string[];
    then: string | string[];
    and_then?: string | string[];
  }>;
}

// --- Stack Selection & Monorepo Bootstrap ---

export type StackCategory = 'frontend' | 'backend' | 'mobile' | 'desktop';

export interface StackOption {
  id: string;
  label: string;
  templateRepo: string;
  skeletonPath: string;
  subfolder: string;
  branch?: string;
}

export type SelectedStacks = Record<StackCategory, StackOption | null>;

export const STACK_OPTIONS: Record<StackCategory, StackOption[]> = {
  frontend: [
    {
      id: 'nextjs',
      label: 'Next.js',
      templateRepo: 'refactory/nextjs-boilerplate',
      skeletonPath: '',
      subfolder: 'frontend',
    },
    {
      id: 'vuejs',
      label: 'Vue.js',
      templateRepo: 'refactory/vuejs-boilerplate',
      skeletonPath: '',
      subfolder: 'frontend',
    },
  ],
  backend: [
    {
      id: 'raiden',
      label: 'Raiden BFF (Go)',
      templateRepo: 'refactory/bff-boilerplate',
      skeletonPath: '',
      subfolder: 'backend',
    },
    {
      id: 'dotnet',
      label: '.NET',
      templateRepo: 'refactory/dotnet-boilerplate',
      skeletonPath: '',
      subfolder: 'backend',
    },
  ],
  mobile: [
    {
      id: 'flutter',
      label: 'Flutter',
      templateRepo: 'refactory/flutter_boilerplate',
      skeletonPath: '',
      subfolder: 'mobile',
      branch: 'template',
    },
  ],
  desktop: [
    {
      id: 'wpf',
      label: 'WPF',
      templateRepo: 'refactory/wpf-boilerplate',
      skeletonPath: '',
      subfolder: 'desktop',
      branch: 'template',
    },
  ],
};

export const STACK_CATEGORY_LABELS: Record<StackCategory, string> = {
  frontend: 'Frontend',
  backend: 'Backend',
  mobile: 'Mobile',
  desktop: 'Desktop',
};

export const EMPTY_SELECTED_STACKS: SelectedStacks = {
  frontend: null,
  backend: null,
  mobile: null,
  desktop: null,
};

export function hasAtLeastOneStack(stacks: SelectedStacks): boolean {
  return Object.values(stacks).some(v => v !== null);
}

export interface StackRecommendationItem {
  category: StackCategory;
  stackId: string;
  reason: string;
}

/** @deprecated Use DynamicStackRecommendationResponse instead */
export interface StackRecommendationResponse {
  recommendations: StackRecommendationItem[];
  summary: string;
}

// --- Dynamic Stack Selection (v2) ---

export type DynamicStackSource = 'entity' | 'document' | 'entity+document';

export interface DynamicStackEntry {
  /** Unique id, e.g. "backend-auth-service" */
  id: string;
  /** Stack category — determines which templates are available */
  category: StackCategory;
  /** Component name from entity or AI analysis, e.g. "auth-service" */
  entityName: string;
  /** Description from entity or AI */
  entityDescription?: string;
  /** Chosen boilerplate template (null = not yet selected) */
  selectedTemplate: StackOption | null;
  /** AI recommendation for this entry */
  aiRecommended?: { stackId: string; reason: string };
  /** Monorepo subfolder, e.g. "backend-auth-service" (flat at root) */
  subfolder: string;
  /** Whether the user has enabled this entry for scaffolding */
  enabled: boolean;
  /** Where the entry was discovered from */
  source?: DynamicStackSource;
  /** Name of matched extracted entity (if any) */
  sourceEntity?: string | null;
}

export interface DynamicStackExcludedEntry {
  entityName: string;
  reason: string;
}

export interface DynamicStackRecommendationResponse {
  stacks: Array<{
    name: string;
    category: StackCategory;
    stackId: string;
    reason: string;
    subfolder: string;
    sourceEntity?: string | null;
    source: DynamicStackSource;
  }>;
  excluded: DynamicStackExcludedEntry[];
  summary: string;
}

export function hasAtLeastOneEnabledStack(
  stacks: DynamicStackEntry[],
): boolean {
  return stacks.some(s => s.enabled && s.selectedTemplate !== null);
}

/** Build DynamicStackEntry[] from AI response, auto-selecting recommended templates */
export function buildDynamicStacksFromRecommendation(
  response: DynamicStackRecommendationResponse,
): DynamicStackEntry[] {
  return response.stacks.map(s => {
    const templates = STACK_OPTIONS[s.category] || [];
    const recommended =
      templates.find(t => t.id === s.stackId) || templates[0] || null;
    return {
      id: s.subfolder,
      category: s.category,
      entityName: s.name,
      entityDescription: s.reason,
      selectedTemplate: recommended
        ? { ...recommended, subfolder: s.subfolder }
        : null,
      aiRecommended: { stackId: s.stackId, reason: s.reason },
      subfolder: s.subfolder,
      enabled: true,
      source: s.source,
      sourceEntity: s.sourceEntity ?? null,
    };
  });
}

/** Deterministic fallback mapping when AI is unavailable */
export function buildFallbackDynamicStacks(
  entities: ExtractedEntity[],
): DynamicStackEntry[] {
  const DEFAULT_TEMPLATES: Record<StackCategory, string> = {
    frontend: 'nextjs',
    backend: 'raiden',
    mobile: 'flutter',
    desktop: 'wpf',
  };

  const entries: DynamicStackEntry[] = [];
  for (const entity of entities) {
    let category: StackCategory | null = null;

    if (entity.kind === 'component' && entity.type === 'service') {
      category = 'backend';
    } else if (entity.kind === 'component' && entity.type === 'website') {
      category = 'frontend';
    } else if (entity.kind === 'screen') {
      if (entity.platform === 'mobile') category = 'mobile';
      else if (entity.platform === 'desktop') category = 'desktop';
      else if (entity.platform === 'web') category = 'frontend';
    }

    if (!category) continue;

    const subfolder = `${category}-${entity.name}`;
    const templates = STACK_OPTIONS[category] || [];
    const defaultId = DEFAULT_TEMPLATES[category];
    const template =
      templates.find(t => t.id === defaultId) || templates[0] || null;

    entries.push({
      id: subfolder,
      category,
      entityName: entity.name,
      entityDescription: entity.description,
      selectedTemplate: template ? { ...template, subfolder } : null,
      subfolder,
      enabled: true,
      source: 'entity',
      sourceEntity: entity.name,
    });
  }
  return entries;
}

/** Compute a simple hash from entities + document for change detection */
export function computeStackDataHash(
  entities: ExtractedEntity[],
  documentText: string,
): string {
  const data = JSON.stringify({
    entities: entities.map(e => ({
      kind: e.kind,
      name: e.name,
      type: e.type,
      platform: e.platform,
      technology: e.technology,
    })),
    docLen: documentText.length,
    docPrefix: documentText.slice(0, 200),
  });
  // Simple DJB2 hash
  let hash = 5381;
  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) + hash + data.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
}

/**
 * Merge new AI recommendations with existing user selections.
 * Preserves manual template overrides and enabled/disabled state for entries
 * that still exist (matched by id). New entries get AI defaults.
 */
export function mergeStackSelections(
  previous: DynamicStackEntry[],
  incoming: DynamicStackEntry[],
): DynamicStackEntry[] {
  const prevMap = new Map(previous.map(e => [e.id, e]));
  return incoming.map(entry => {
    const prev = prevMap.get(entry.id);
    if (prev) {
      // Preserve user's template choice and enabled state
      return {
        ...entry,
        selectedTemplate: prev.selectedTemplate,
        enabled: prev.enabled,
      };
    }
    return entry;
  });
}

// --- Story Generation & Bootstrap ---

export interface StoryGenerationPayload {
  prdText: string;
  entities: Array<{
    kind: string;
    name: string;
    description?: string;
    type?: string;
  }>;
  projectName: string;
  systemName: string;
  techStack: {
    frontend: string;
    backend: string;
    database: string;
  };
}

export interface BootstrapPayload {
  projectName: string;
  description?: string;
  owner: string;
  systemName: string;
  org: string;
  /** @deprecated Use dynamicStacks instead */
  selectedStacks?: SelectedStacks;
  dynamicStacks?: DynamicStackEntry[];
  /** @deprecated Use dynamicStacks instead. Kept for story generation compat. */
  techStack?: {
    frontend: string;
    backend: string;
    database: string;
  };
  stories?: GeneratedStory[];
  prdText?: string;
  entities?: Array<{
    kind: string;
    name: string;
    description?: string;
    type?: string;
  }>;
  relationships?: Array<{
    sourceIndex: number;
    targetIndex: number;
  }>;
  projectId?: string;
}

export interface BootstrapResult {
  success: boolean;
  repoName?: string;
  repoUrl?: string;
  /** @deprecated Old dual-repo field, kept for backward compat with old state */
  frontendRepo?: string;
  /** @deprecated Old dual-repo field, kept for backward compat with old state */
  backendRepo?: string;
  projectId?: string;
  automationProjectId?: string;
  error?: string;
  results: Array<{ step: string; status: string; detail?: string }>;
}

// --- Repo Stories (from Gitea) ---

export interface DesignEntry {
  url: string;
  generatedAt: string;
}

export interface RepoStoryFile {
  fileName: string;
  path: string;
  htmlUrl: string;
  category: string;
  name: string;
  title: string;
  description: string;
  objective: string;
  businessFlow: string;
  difficulty?: 'trivial' | 'moderate' | 'complex';
  storyPoints?: number;
  actors: Array<{ name: string; description?: string }>;
  acceptanceCriteria: Array<Record<string, unknown>>;
  nodes: string[];
  evidenceUrl?: string;
  designs?: DesignEntry[];
  error?: string;
}

export interface RepoStoriesResponse {
  stories: RepoStoryFile[];
  repoUrl: string | null;
}

export interface MockupResult {
  html: string;
  cached: boolean;
  storyName: string;
  model?: string;
  durationMs?: number;
}

export type MockupJobStatus =
  | { status: 'none' }
  | { status: 'pending'; startedAt: string }
  | { status: 'in_progress'; startedAt: string; taskId?: string }
  | {
      status: 'done';
      html: string;
      model?: string;
      durationMs?: number;
      startedAt: string;
    }
  | { status: 'failed'; error?: string; startedAt?: string };

export interface MockupStartResult {
  status: string;
  startedAt: string;
}

export type FigmaMockupJobStatus =
  | { status: 'none' }
  | { status: 'pending'; startedAt: string }
  | { status: 'in_progress'; startedAt: string; taskId?: string }
  | { status: 'done'; figmaUrl: string; startedAt: string }
  | { status: 'failed'; error?: string; startedAt?: string };

export interface StoryUpdatePayload {
  title: string;
  description: string;
  objective: string;
  businessFlow: string;
  difficulty?: 'trivial' | 'moderate' | 'complex';
  storyPoints?: number;
  actors: Array<{ name: string; description?: string }>;
  acceptanceCriteria: Array<{
    scenario: string;
    id?: string;
    type?: 'positive' | 'negative';
    actor?: string[];
    given?: string | string[];
    when?: string | string[];
    and_when?: string | string[];
    then?: string | string[];
    and_then?: string | string[];
  }>;
  nodes: string[];
}

export interface StoryEstimation {
  projectId: string;
  storyPath: string;
  difficulty: 'trivial' | 'moderate' | 'complex';
  storyPoints: number;
  updatedAt: string;
}

export interface RepoDocument {
  fileName: string;
  s3Key: string;
  size: number;
  lastModified: string | null;
}

export interface RepoDocumentsResponse {
  documents: RepoDocument[];
}

export type StoryGenerationPhase =
  | 'reading-documents'
  | 'reading-entities'
  | 'validating-entities'
  | 'generating'
  | 'reviewing'
  | 'committing'
  | 'complete'
  | 'failed';

export interface StoryGenerationProgress {
  phase: StoryGenerationPhase;
  message: string;
  model?: string;
  provider?: string;
  storyCount?: number;
  startedAt?: string;
  completedAt?: string;
}

/** Persisted generation status — stored in project record, survives navigation */
export interface GenerationStatus {
  status: 'idle' | 'running' | 'complete' | 'failed';
  steps: StoryGenerationProgress[];
  startedAt?: string;
  error?: string;
}

/** DBML generation progress step */
export interface DbmlGenerationProgress {
  phase: string;
  message: string;
  model?: string;
  provider?: string;
  startedAt?: string;
  completedAt?: string;
}

/** DBML generation status — stored in project record */
export interface DbmlGenerationStatus {
  status: 'idle' | 'running' | 'complete' | 'failed';
  steps: DbmlGenerationProgress[];
  startedAt?: string;
  error?: string;
  dbml?: string;
}

/** Diagram generation progress step */
export interface DiagramGenerationProgress {
  phase: string;
  message: string;
  startedAt?: string;
  completedAt?: string;
}

/** Diagram generation status — stored in project record */
export interface DiagramGenerationStatus {
  status: 'idle' | 'running' | 'complete' | 'failed';
  steps: DiagramGenerationProgress[];
  startedAt?: string;
  error?: string;
}

/** Regeneration phase identifier */
export type RegenerationPhase =
  | 'reading-documents'
  | 'extracting-entities'
  | 'updating-diagram'
  | 'updating-repo'
  | 'clearing-stories'
  | 'generating-stories'
  | 'committing'
  | 'complete'
  | 'failed';

/** Regeneration progress step */
export interface RegenerationProgress {
  phase: RegenerationPhase;
  message: string;
  startedAt?: string;
  completedAt?: string;
}

/** Persisted regeneration status — stored in project record, survives navigation */
export interface RegenerationStatus {
  status: 'idle' | 'running' | 'complete' | 'failed';
  steps: RegenerationProgress[];
  startedAt?: string;
  error?: string;
}

export interface CheckReposResponse {
  repoName: string;
  repoExists: boolean;
  /** @deprecated Old dual-repo field */
  frontendRepo?: string;
  /** @deprecated Old dual-repo field */
  backendRepo?: string;
  /** @deprecated Old dual-repo field */
  frontendExists?: boolean;
  /** @deprecated Old dual-repo field */
  backendExists?: boolean;
}

// --- Start Agentic Session types ---

export interface StartAgenticPayload {
  taskIds: string[];
  config: {
    mode: 'agents' | 'time';
    agentCount?: number;
    estimatedDays?: number;
  };
  createdBy?: string;
  /** Repository URL for the agentic session - required if not in project state */
  repoUrl?: string;
  /** Base branch for the agentic session (defaults to 'main') */
  baseBranch?: string;
}

export interface StartAgenticResponse {
  success: boolean;
  assignmentRequestId: string;
  taskIds: string[];
  tasksCreated: number;
  error?: string;
}

// --- AI Workflow Task types (from AI Workflow API) ---

export type AiWorkflowTaskStatus =
  | 'draft'
  | 'ready'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'cancelled';

export type AiWorkflowExecutionStatus =
  | 'ready'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface AiWorkflowTaskAssignment {
  id: string;
  agent_id: string;
  agent_name: string;
  status: AiWorkflowExecutionStatus;
  started_at: string | null;
  completed_at: string | null;
}

export interface AiWorkflowTaskExecution {
  id: string;
  step: string;
  sequence: number;
  status: AiWorkflowExecutionStatus;
  result: Record<string, unknown> | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  assignments?: AiWorkflowTaskAssignment[];
}

export interface AiWorkflowTask {
  id: string;
  project_id: string | null;
  category: {
    id: string;
    name: string;
  };
  title: string;
  data: {
    repo_url?: string;
    base_branch?: string;
    story_path?: string;
    [key: string]: unknown;
  };
  priority: number;
  status: AiWorkflowTaskStatus;
  executions: AiWorkflowTaskExecution[];
  created_at: string;
  updated_at: string;
}

export interface AiWorkflowTasksResponse {
  tasks: AiWorkflowTask[];
  total: number;
  page: number;
  pageSize: number;
}

// --- Assignment Request types (from AI Workflow API) ---

export type AssignmentRequestStatus =
  | 'pending'
  | 'assigning'
  | 'assigned'
  | 'failed';

export interface AssignmentRequestTask {
  id: string;
  title: string;
  status: AiWorkflowTaskStatus;
}

export interface AssignmentRequest {
  id: string;
  project_id: string;
  created_by: string;
  status: AssignmentRequestStatus;
  params: {
    strategy: 'fixed_agents' | 'fixed_duration';
    requested_agents?: number;
    requested_days?: number;
  };
  tasks: AssignmentRequestTask[];
  created_at: string;
  updated_at: string;
}

export interface AssignmentRequestsResponse {
  requests: AssignmentRequest[];
  total: number;
  page: number;
  pageSize: number;
}

// --- Automation DB types ---

export type StepName =
  | 'story-clarifier'
  | 'story-answer-clarifier'
  | 'clarifier-review'
  | 'story-to-spec'
  | 'story-apply'
  | 'verifier-plan'
  | 'verifier-review'
  | 'verifier-apply'
  | 'verifier-run'
  | 'verifier-fix'
  | 'create-pr'
  | 'notify-client';

export type TaskStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'waiting'
  | 'skipped'
  | 'cancelled';

export type PipelineStatus =
  | 'idle'
  | 'running'
  | 'paused'
  | 'error'
  | 'stopped';

export const STEP_ORDER: StepName[] = [
  'story-clarifier',
  'story-answer-clarifier',
  'clarifier-review',
  'story-to-spec',
  'story-apply',
  'verifier-plan',
  'verifier-review',
  'verifier-apply',
  'verifier-run',
  'verifier-fix',
  'create-pr',
  'notify-client',
];

export const STEP_LABELS: Record<StepName, string> = {
  'story-clarifier': 'Analyzing Requirements',
  'story-answer-clarifier': 'Answering Questions',
  'clarifier-review': 'Reviewing Answers',
  'story-to-spec': 'Creating Specification',
  'story-apply': 'Writing Code',
  'verifier-plan': 'Planning Tests',
  'verifier-review': 'Reviewing Test Plan',
  'verifier-apply': 'Writing Tests',
  'verifier-run': 'Running Tests',
  'verifier-fix': 'Fixing Issues',
  'create-pr': 'Creating Pull Request',
  'notify-client': 'Done — Notifying Team',
};

export interface AutomationProject {
  id: string;
  name: string;
  displayName: string;
  repoUrl: string;
  projectType: 'web' | 'backend';
  defaultOwner: string;
  storyCount?: number;
}

export interface AutomationStory {
  id: string;
  projectId: string;
  name: string;
  module: string;
  currentStep: StepName;
  stepStatus: TaskStatus;
  branchName: string | null;
  prNumber: number | null;
  prUrl: string | null;
  errorMessage: string | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  // AI analytics aggregates
  totalAiCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalDurationMs: number;
  bugCount: number;
  bugsResolved: number;
  primaryModel: string | null;
  // Agentic session tracking
  agenticId: string | null;
  batchAgenticId: string | null;
}

export interface StoryLogEntry {
  id: string;
  storyId: string;
  pipelineId: string;
  step: StepName;
  status: TaskStatus;
  level: string;
  message: string;
  durationMs: number | null;
  createdAt: string;
  // AI tracking per-step
  modelName: string | null;
  aiCallCount: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
}

export interface AutomationPipeline {
  id: string;
  projectId: string;
  name: string;
  status: PipelineStatus;
  currentStoryId: string | null;
  currentStep: StepName | null;
}

export interface AutomationStoriesResponse {
  stories: AutomationStory[];
  pipeline: AutomationPipeline | null;
}

// ─── Uptime / Gatus types ─────────────────────────────────────────────────
export interface UptimeEndpoint {
  name: string;
  key: string;
  url: string;
  group: string;
  uptime: { '1h': number; '24h': number; '7d': number };
  events: Array<{ type: string; timestamp: string }>;
  results: Array<{
    status: number;
    duration: number;
    success: boolean;
    timestamp: string;
    conditionResults: Array<{ condition: string; success: boolean }>;
  }>;
}

export interface UptimeSummary {
  totalEndpoints: number;
  healthyCount: number;
  unhealthyCount: number;
  overallUptime24h: number;
  overallUptime7d: number;
  avgResponseTime: number;
  incidentCount30d: number;
}

export interface UptimeData {
  endpoints: UptimeEndpoint[];
  summary: UptimeSummary;
}

export interface UptimeIncident {
  endpointName: string;
  startedAt: string;
  resolvedAt: string | null;
  durationMs: number | null;
}

export interface ProjOnboardApi {
  getHealth(): Promise<HealthResponse>;
  extract(
    documentText: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
  ): Promise<ExtractionResponse>;
  getAiSettings(): Promise<AiSettingsResponse>;
  saveAiSettings(config: AiSettingsPayload): Promise<{ success: boolean }>;
  deleteAiSettings(): Promise<{ success: boolean }>;
  getAiUsage(): Promise<AiUsageSummary>;
  testAiConnection(
    config: AiSettingsPayload,
  ): Promise<{ success: boolean; message: string }>;
  uploadDocument(
    file: File,
    owner?: string,
    projectName?: string,
  ): Promise<UploadDocumentResponse>;
  registerEntities(
    entities: ExtractedEntity[],
    systemName?: string,
  ): Promise<RegisterEntitiesResponse>;
  listProjects(): Promise<ProjectSummary[]>;
  getProject(id: string): Promise<ProjectDetail>;
  createProject(data: CreateProjectPayload): Promise<ProjectDetail>;
  updateProject(id: string, data: UpdateProjectPayload): Promise<ProjectDetail>;
  updateProjectStatus(
    id: string,
    status: ProjectStatus,
  ): Promise<ProjectDetail>;
  listGiteaOrgs(): Promise<GiteaOrg[]>;
  generateStories(
    payload: StoryGenerationPayload,
  ): Promise<{ stories: GeneratedStory[] }>;
  bootstrapProject(payload: BootstrapPayload): Promise<BootstrapResult>;
  listAutomationProjects(): Promise<AutomationProject[]>;
  linkAutomationProject(
    projectId: string,
    automationProjectId: string | null,
  ): Promise<ProjectDetail>;
  getAutomationStories(projectId: string): Promise<AutomationStoriesResponse>;
  getAutomationStoryLogs(storyId: string): Promise<StoryLogEntry[]>;
  deleteProject(id: string): Promise<{ success: boolean }>;
  getProjectRepoStories(projectId: string): Promise<RepoStoriesResponse>;
  updateStory(
    projectId: string,
    storyPath: string,
    payload: StoryUpdatePayload,
  ): Promise<{ success: boolean }>;
  getStoryEstimations(
    projectId: string,
  ): Promise<Record<string, StoryEstimation>>;
  setStoryEstimation(
    projectId: string,
    storyPath: string,
    difficulty: 'trivial' | 'moderate' | 'complex',
    storyPoints: number,
  ): Promise<StoryEstimation>;
  getProjectRepoDocuments(projectId: string): Promise<RepoDocumentsResponse>;
  downloadDocument(
    projectId: string,
    s3Key: string,
    fileName: string,
  ): Promise<void>;
  generateProjectStories(projectId: string): Promise<void>;
  getGenerationStatus(projectId: string): Promise<GenerationStatus>;
  generateProjectDiagram(projectId: string): Promise<void>;
  getDiagramGenerationStatus(
    projectId: string,
  ): Promise<DiagramGenerationStatus>;
  generateDbml(projectId: string): Promise<void>;
  getDbmlGenerationStatus(projectId: string): Promise<DbmlGenerationStatus>;
  regenerateProject(projectId: string): Promise<void>;
  getRegenerationStatus(projectId: string): Promise<RegenerationStatus>;
  getDbml(projectId: string): Promise<{ dbml: string | null }>;
  updateDbml(projectId: string, dbml: string): Promise<{ success: boolean }>;
  renderDbml(projectId: string, dbml: string): Promise<{ svg: string }>;
  checkProjectRepos(
    org: string,
    projectName: string,
  ): Promise<CheckReposResponse>;
  deleteDocument(
    projectId: string,
    s3Key: string,
  ): Promise<{ success: boolean }>;
  uploadProjectDocument(
    projectId: string,
    file: File,
  ): Promise<UploadDocumentResponse>;
  getDocumentTags(
    projectId: string,
    s3Key: string,
  ): Promise<{ s3Key: string; tags: Record<string, string> }>;
  setDocumentTags(
    projectId: string,
    s3Key: string,
    tags: Record<string, string>,
  ): Promise<{ s3Key: string; tags: Record<string, string> }>;
  getTranscriptionStatus(
    projectId: string,
    s3Key: string,
  ): Promise<TranscriptionStatusResponse>;
  validateRepo(url: string, token?: string): Promise<ValidateRepoResponse>;
  scanRepos(
    repos: Array<{ url: string; token?: string }>,
  ): Promise<ScanReposResponse>;
  createRepoPrs(payload: {
    repos: Array<{ url: string; token?: string }>;
    entities: ExtractedEntity[];
    relationships: ExtractedRelationship[];
    systemName: string;
    owner: string;
  }): Promise<CreateRepoPrsResponse>;
  getProjectUptime(projectId: string): Promise<UptimeData>;
  registerUptimeEndpoints(
    projectId: string,
  ): Promise<{ keys: string[]; errors: string[] }>;
  deleteUptimeEndpoint(projectId: string, endpointKey: string): Promise<void>;
  getUptimeIncidents(
    projectId: string,
  ): Promise<{ incidents: UptimeIncident[] }>;
  updateAutomationStoryStep(
    storyId: string,
    currentStep: string,
  ): Promise<{ success: boolean }>;
  listRepoRefs(owner: string, repo: string): Promise<GiteaRefsResponse>;
  // Manual task phase management
  getManualTaskPhases(projectId: string): Promise<ManualTaskPhasesResponse>;
  updateManualTaskPhase(
    projectId: string,
    taskId: string,
    phase: ManualPhaseKey,
  ): Promise<{
    id: string;
    projectId: string;
    taskId: string;
    phase: ManualPhaseKey;
  }>;
  // Start agentic session
  startAgenticSession(
    projectId: string,
    payload: StartAgenticPayload,
  ): Promise<StartAgenticResponse>;
  // Get agentic tasks from AI Workflow API
  listAgenticTasks(
    projectId: string,
    options?: { page?: number; pageSize?: number; status?: string },
  ): Promise<AiWorkflowTasksResponse>;
  getAgenticTaskDetail(
    projectId: string,
    taskId: string,
  ): Promise<AiWorkflowTask>;
  // Cancel an agentic task
  cancelAgenticTask(
    projectId: string,
    taskId: string,
  ): Promise<{ success: boolean; task?: AiWorkflowTask }>;
  // List assignment requests (agentic batches) for a project
  listAssignmentRequests(
    projectId: string,
    options?: { page?: number; pageSize?: number; status?: string },
  ): Promise<AssignmentRequestsResponse>;
  recommendStacks(input: {
    projectDescription: string;
    documentText: string;
    entities: Array<{
      kind: string;
      name: string;
      description?: string;
      type?: string;
      platform?: string;
      technology?: string;
    }>;
    conversationHistory: Array<{ role: string; content: string }>;
  }): Promise<DynamicStackRecommendationResponse>;
  getStoryMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      businessFlow?: string;
      actors?: Array<{ name: string; description?: string }>;
    },
  ): Promise<MockupStartResult>;
  regenerateStoryMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      businessFlow?: string;
      actors?: Array<{ name: string; description?: string }>;
    },
  ): Promise<MockupStartResult>;
  getMockupStatus(
    projectId: string,
    storyPath: string,
  ): Promise<MockupJobStatus>;
  startFigmaMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      actors?: Array<{ name: string; description?: string } | string>;
      acceptanceCriteria?: Array<unknown>;
      companyName?: string;
      projectName?: string;
    },
  ): Promise<MockupStartResult>;
  regenerateFigmaMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      actors?: Array<{ name: string; description?: string } | string>;
      acceptanceCriteria?: Array<unknown>;
      companyName?: string;
      projectName?: string;
    },
  ): Promise<MockupStartResult>;
  getFigmaMockupStatus(
    projectId: string,
    storyPath: string,
  ): Promise<FigmaMockupJobStatus>;
  cancelHtmlMockup(projectId: string, storyPath: string): Promise<void>;
  cancelFigmaMockup(projectId: string, storyPath: string): Promise<void>;
  getFigmaMe(): Promise<{
    id: string;
    handle: string;
    email: string;
    imgUrl?: string;
  }>;
}

export const projOnboardApiRef = createApiRef<ProjOnboardApi>({
  id: 'plugin.proj-onboard.api',
});

export class ProjOnboardClient implements ProjOnboardApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('proj-onboard');
  }

  async getHealth(): Promise<HealthResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`);
    }
    return response.json();
  }

  async extract(
    documentText: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
  ): Promise<ExtractionResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentText, conversationHistory }),
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(body, `Extraction failed: ${response.statusText}`),
      );
    }
    return response.json();
  }

  async recommendStacks(input: {
    projectDescription: string;
    documentText: string;
    entities: Array<{
      kind: string;
      name: string;
      description?: string;
      type?: string;
      platform?: string;
      technology?: string;
    }>;
    conversationHistory: Array<{ role: string; content: string }>;
  }): Promise<DynamicStackRecommendationResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/recommend-stacks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Stack recommendation failed: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async getStoryMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      businessFlow?: string;
      actors?: Array<{ name: string; description?: string }>;
    },
  ): Promise<MockupStartResult> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/mockup?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(story ?? {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, `Failed to get mockup (${response.status})`),
      );
    }
    return response.json();
  }

  async regenerateStoryMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      businessFlow?: string;
      actors?: Array<{ name: string; description?: string }>;
    },
  ): Promise<MockupStartResult> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/mockup/regenerate?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(story ?? {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to regenerate mockup (${response.status})`,
        ),
      );
    }
    return response.json();
  }

  async getMockupStatus(
    projectId: string,
    storyPath: string,
  ): Promise<MockupJobStatus> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/mockup?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, { method: 'GET' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to get mockup status (${response.status})`,
        ),
      );
    }
    return response.json();
  }

  async startFigmaMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      actors?: Array<{ name: string; description?: string } | string>;
      acceptanceCriteria?: Array<unknown>;
      companyName?: string;
      projectName?: string;
    },
  ): Promise<MockupStartResult> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/figma-mockup?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(story ?? {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to start Figma mockup (${response.status})`,
        ),
      );
    }
    return response.json();
  }

  async regenerateFigmaMockup(
    projectId: string,
    storyPath: string,
    story?: {
      title?: string;
      description?: string;
      actors?: Array<{ name: string; description?: string } | string>;
      acceptanceCriteria?: Array<unknown>;
      companyName?: string;
      projectName?: string;
    },
  ): Promise<MockupStartResult> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/figma-mockup/regenerate?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(story ?? {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to regenerate Figma mockup (${response.status})`,
        ),
      );
    }
    return response.json();
  }

  async getFigmaMockupStatus(
    projectId: string,
    storyPath: string,
  ): Promise<FigmaMockupJobStatus> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/figma-mockup?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, { method: 'GET' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to get Figma mockup status (${response.status})`,
        ),
      );
    }
    return response.json();
  }

  async cancelHtmlMockup(projectId: string, storyPath: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/mockup?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to cancel HTML mockup (${response.status})`,
        ),
      );
    }
  }

  async cancelFigmaMockup(projectId: string, storyPath: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/projects/${encodeURIComponent(projectId)}/stories/figma-mockup?storyPath=${encodeURIComponent(storyPath)}`;
    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to cancel Figma mockup (${response.status})`,
        ),
      );
    }
  }

  async getFigmaMe(): Promise<{
    id: string;
    handle: string;
    email: string;
    imgUrl?: string;
  }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/figma/me`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to get Figma user info (${response.status})`,
        ),
      );
    }
    return response.json();
  }

  async getAiSettings(): Promise<AiSettingsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/settings/ai-config`);
    if (!response.ok) {
      throw new Error(`Failed to get AI settings: ${response.statusText}`);
    }
    return response.json();
  }

  async saveAiSettings(
    payload: AiSettingsPayload,
  ): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/settings/ai-config`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to save AI settings: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async deleteAiSettings(): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/settings/ai-config`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      throw new Error(`Failed to delete AI settings: ${response.statusText}`);
    }
    return response.json();
  }

  async getAiUsage(): Promise<AiUsageSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/ai-usage`);
    if (!response.ok) {
      throw new Error(`Failed to get AI usage: ${response.statusText}`);
    }
    return response.json();
  }

  async testAiConnection(
    payload: AiSettingsPayload,
  ): Promise<{ success: boolean; message: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/settings/test-connection`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Connection test failed: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async uploadDocument(
    file: File,
    owner?: string,
    projectName?: string,
  ): Promise<UploadDocumentResponse> {
    const baseUrl = await this.getBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    if (owner) formData.append('owner', owner);
    if (projectName) formData.append('projectName', projectName);
    const response = await this.fetchApi.fetch(`${baseUrl}/documents/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to upload document: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async registerEntities(
    entities: ExtractedEntity[],
    systemName?: string,
  ): Promise<RegisterEntitiesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/catalog/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entities, systemName }),
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to register entities: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async listProjects(): Promise<ProjectSummary[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/projects`);
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to list projects: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async getProject(id: string): Promise<ProjectDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(id)}`,
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to get project: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async createProject(data: CreateProjectPayload): Promise<ProjectDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to create project: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async updateProject(
    id: string,
    data: UpdateProjectPayload,
  ): Promise<ProjectDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to update project: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async updateProjectStatus(
    id: string,
    status: ProjectStatus,
  ): Promise<ProjectDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(id)}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to update project status: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async listGiteaOrgs(): Promise<GiteaOrg[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/gitea/orgs`);
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to list Gitea orgs: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async listRepoRefs(owner: string, repo: string): Promise<GiteaRefsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/gitea/repos/${encodeURIComponent(owner)}/${encodeURIComponent(
        repo,
      )}/refs`,
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to list repo refs: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async generateStories(
    payload: StoryGenerationPayload,
  ): Promise<{ stories: GeneratedStory[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/stories/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Story generation failed: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async bootstrapProject(payload: BootstrapPayload): Promise<BootstrapResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/bootstrap`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(body, `Bootstrap failed: ${response.statusText}`),
      );
    }
    return response.json();
  }

  async listAutomationProjects(): Promise<AutomationProject[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/automation-projects`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to list automation projects: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async linkAutomationProject(
    projectId: string,
    automationProjectId: string | null,
  ): Promise<ProjectDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/automation-link`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ automationProjectId }),
      },
    );
    if (!response.ok) {
      throw new Error(
        `Failed to link automation project: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async getAutomationStories(
    projectId: string,
  ): Promise<AutomationStoriesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/automation-stories`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to get automation stories: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async getAutomationStoryLogs(storyId: string): Promise<StoryLogEntry[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/automation-stories/${encodeURIComponent(storyId)}/logs`,
    );
    if (!response.ok) {
      throw new Error(`Failed to get story logs: ${response.statusText}`);
    }
    return response.json();
  }

  async deleteProject(id: string): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to delete project: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async getProjectRepoStories(projectId: string): Promise<RepoStoriesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/repo-stories`,
    );
    if (!response.ok) {
      throw new Error(`Failed to get repo stories: ${response.statusText}`);
    }
    return response.json();
  }

  async updateStory(
    projectId: string,
    storyPath: string,
    payload: StoryUpdatePayload,
  ): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/stories?path=${encodeURIComponent(storyPath)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to update story: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async getStoryEstimations(
    projectId: string,
  ): Promise<Record<string, StoryEstimation>> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/story-estimations`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch story estimations: ${response.statusText}`,
      );
    }
    const body = await response.json();
    return body.data ?? {};
  }

  async setStoryEstimation(
    projectId: string,
    storyPath: string,
    difficulty: 'trivial' | 'moderate' | 'complex',
    storyPoints: number,
  ): Promise<StoryEstimation> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/story-estimations`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          story_path: storyPath,
          difficulty,
          story_points: storyPoints,
        }),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to save story estimation: ${response.statusText}`,
        ),
      );
    }
    const body = await response.json();
    return body.data;
  }

  /** Update an automation story's current step (e.g., move to todo) */
  async updateAutomationStoryStep(
    storyId: string,
    currentStep: string,
  ): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/automation-stories/${encodeURIComponent(storyId)}/step`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentStep }),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to update story step: ${response.statusText}`);
    }
    return response.json();
  }

  async getProjectRepoDocuments(
    projectId: string,
  ): Promise<RepoDocumentsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/repo-documents`,
    );
    if (!response.ok) {
      throw new Error(`Failed to get repo documents: ${response.statusText}`);
    }
    return response.json();
  }

  async downloadDocument(
    projectId: string,
    s3Key: string,
    fileName: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/documents/download?key=${encodeURIComponent(s3Key)}`,
    );
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async generateProjectStories(projectId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/generate-stories`,
      { method: 'POST' },
    );

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        body?.error || `Story generation failed: ${response.statusText}`,
      );
    }
  }

  async getGenerationStatus(projectId: string): Promise<GenerationStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/generation-status`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to get generation status: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async generateProjectDiagram(projectId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/generate-diagram`,
      { method: 'POST' },
    );

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        body?.error || `Diagram generation failed: ${response.statusText}`,
      );
    }
  }

  async getDiagramGenerationStatus(
    projectId: string,
  ): Promise<DiagramGenerationStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/diagram-generation-status`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to get diagram generation status: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async generateDbml(projectId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/generate-dbml`,
      { method: 'POST' },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        body?.error || `DBML generation failed: ${response.statusText}`,
      );
    }
  }

  async getDbmlGenerationStatus(
    projectId: string,
  ): Promise<DbmlGenerationStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/dbml-generation-status`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to get DBML generation status: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async regenerateProject(projectId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/regenerate`,
      { method: 'POST' },
    );

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        body?.error || `Regeneration failed: ${response.statusText}`,
      );
    }
  }

  async getRegenerationStatus(projectId: string): Promise<RegenerationStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/regeneration-status`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to get regeneration status: ${response.statusText}`,
      );
    }
    return response.json();
  }

  async getDbml(projectId: string): Promise<{ dbml: string | null }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/dbml`,
    );
    if (!response.ok) {
      throw new Error(`Failed to get DBML: ${response.statusText}`);
    }
    return response.json();
  }

  async updateDbml(
    projectId: string,
    dbml: string,
  ): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/dbml`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbml }),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to update DBML: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async renderDbml(projectId: string, dbml: string): Promise<{ svg: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/dbml/render`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbml }),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to render DBML: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async checkProjectRepos(
    org: string,
    projectName: string,
  ): Promise<CheckReposResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ org, projectName });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/check-repos?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error(`Failed to check repos: ${response.statusText}`);
    }
    return response.json();
  }

  async deleteDocument(
    projectId: string,
    s3Key: string,
  ): Promise<{ success: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/documents?key=${encodeURIComponent(s3Key)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to delete document: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async uploadProjectDocument(
    projectId: string,
    file: File,
  ): Promise<UploadDocumentResponse> {
    const baseUrl = await this.getBaseUrl();
    // First get the project to know owner + name for S3 prefix
    const project = await this.getProject(projectId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('owner', project.owner);
    formData.append('projectName', project.name);
    formData.append('projectId', projectId);
    const response = await this.fetchApi.fetch(`${baseUrl}/documents/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to upload document: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async getDocumentTags(
    projectId: string,
    s3Key: string,
  ): Promise<{ s3Key: string; tags: Record<string, string> }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/documents/tags?s3Key=${encodeURIComponent(s3Key)}`,
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to get document tags: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async setDocumentTags(
    projectId: string,
    s3Key: string,
    tags: Record<string, string>,
  ): Promise<{ s3Key: string; tags: Record<string, string> }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/documents/tags`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key, tags }),
      },
    );
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ error: response.statusText }));
      throw new Error(
        extractErrorMessage(
          body,
          `Failed to set document tags: ${response.statusText}`,
        ),
      );
    }
    return response.json();
  }

  async getTranscriptionStatus(
    projectId: string,
    s3Key: string,
  ): Promise<TranscriptionStatusResponse> {
    const baseUrl = await this.getBaseUrl();
    const resp = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/documents/transcription-status?s3Key=${encodeURIComponent(s3Key)}`,
    );
    if (!resp.ok) {
      throw new Error(`Failed to get transcription status: ${resp.statusText}`);
    }
    return resp.json();
  }

  async validateRepo(
    url: string,
    token?: string,
  ): Promise<ValidateRepoResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/repos/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, token }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(body, 'Failed to validate repo'));
    }
    return response.json();
  }

  async scanRepos(
    repos: Array<{ url: string; token?: string }>,
  ): Promise<ScanReposResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/repos/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repos }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(body, 'Failed to scan repos'));
    }
    return response.json();
  }

  async createRepoPrs(payload: {
    repos: Array<{ url: string; token?: string }>;
    entities: ExtractedEntity[];
    relationships: ExtractedRelationship[];
    systemName: string;
    owner: string;
  }): Promise<CreateRepoPrsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/repos/create-pr`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(body, 'Failed to create PRs'));
    }
    return response.json();
  }

  async getProjectUptime(projectId: string): Promise<UptimeData> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/telemetry/uptime`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(body, 'Failed to fetch uptime data'));
    }
    return response.json();
  }

  async registerUptimeEndpoints(
    projectId: string,
  ): Promise<{ keys: string[]; errors: string[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/telemetry/uptime/register`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to register endpoints'),
      );
    }
    return response.json();
  }

  async deleteUptimeEndpoint(
    projectId: string,
    endpointKey: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/telemetry/uptime/endpoints/${encodeURIComponent(endpointKey)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(body, 'Failed to delete endpoint'));
    }
  }

  async getUptimeIncidents(
    projectId: string,
  ): Promise<{ incidents: UptimeIncident[] }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/telemetry/uptime/incidents`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(body, 'Failed to fetch incidents'));
    }
    return response.json();
  }

  async getManualTaskPhases(
    projectId: string,
  ): Promise<ManualTaskPhasesResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/manual-tasks`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to fetch manual task phases'),
      );
    }
    return response.json();
  }

  async updateManualTaskPhase(
    projectId: string,
    taskId: string,
    phase: ManualPhaseKey,
  ): Promise<{
    id: string;
    projectId: string;
    taskId: string;
    phase: ManualPhaseKey;
  }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/manual-tasks/${encodeURIComponent(taskId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase }),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to update manual task phase'),
      );
    }
    return response.json();
  }

  async startAgenticSession(
    projectId: string,
    payload: StartAgenticPayload,
  ): Promise<StartAgenticResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(projectId)}/start-agentic`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to start agentic session'),
      );
    }
    return response.json();
  }

  async listAgenticTasks(
    projectId: string,
    options?: { page?: number; pageSize?: number; status?: string },
  ): Promise<AiWorkflowTasksResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.pageSize) params.append('page_size', String(options.pageSize));
    if (options?.status) params.append('status', options.status);

    const url = `${baseUrl}/projects/${encodeURIComponent(
      projectId,
    )}/agentic-tasks${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to fetch agentic tasks'),
      );
    }
    return response.json();
  }

  async getAgenticTaskDetail(
    projectId: string,
    taskId: string,
  ): Promise<AiWorkflowTask> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/agentic-tasks/${encodeURIComponent(taskId)}`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to fetch agentic task detail'),
      );
    }
    return response.json();
  }

  async cancelAgenticTask(
    projectId: string,
    taskId: string,
  ): Promise<{ success: boolean; task?: AiWorkflowTask }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${encodeURIComponent(
        projectId,
      )}/agentic-tasks/${encodeURIComponent(taskId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to cancel agentic task'),
      );
    }
    return response.json();
  }

  async listAssignmentRequests(
    projectId: string,
    options?: { page?: number; pageSize?: number; status?: string },
  ): Promise<AssignmentRequestsResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.pageSize) params.append('page_size', String(options.pageSize));
    if (options?.status) params.append('status', options.status);

    const url = `${baseUrl}/projects/${encodeURIComponent(
      projectId,
    )}/assignment-requests${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        extractErrorMessage(body, 'Failed to fetch assignment requests'),
      );
    }
    return response.json();
  }
}

// ─── Client-side cost estimation ──────────────────────────────────────────────

/** Model → { input multiplier, output multiplier } for GitHub Models pricing. */
const MODEL_COST_MULTIPLIERS: Record<
  string,
  { input: number; output: number }
> = {
  'gpt-4o': { input: 25, output: 100 },
  'gpt-4o-mini': { input: 1.5, output: 6 },
  'gpt-4.1': { input: 20, output: 80 },
  'gpt-4.1-mini': { input: 4, output: 16 },
  'gpt-4.1-nano': { input: 1, output: 4 },
  o1: { input: 150, output: 600 },
  'o1-mini': { input: 30, output: 120 },
  'o1-preview': { input: 150, output: 600 },
  o3: { input: 100, output: 400 },
  'o3-mini': { input: 11, output: 44 },
  'o4-mini': { input: 11, output: 44 },
  'meta-llama-3.1-405b-instruct': { input: 53, output: 53 },
  'meta-llama-3.1-70b-instruct': { input: 3.5, output: 4 },
  'meta-llama-3.1-8b-instruct': { input: 0.3, output: 0.6 },
  'llama-3.3-70b-instruct': { input: 3.5, output: 4 },
  'mistral-large': { input: 20, output: 60 },
  'mistral-large-2411': { input: 20, output: 60 },
  'mistral-small': { input: 1, output: 3 },
  'cohere-command-r-plus': { input: 25, output: 100 },
  'cohere-command-r': { input: 1.5, output: 6 },
  'deepseek-r1': { input: 5.5, output: 21.9 },
  'deepseek-v3': { input: 2.7, output: 11 },
};

/**
 * Estimate cost in USD using the GitHub Models pricing formula.
 * Returns undefined if model is unknown or tokens are missing.
 */
export function estimateCostUsd(
  model: string | null | undefined,
  inputTokens: number | null | undefined,
  outputTokens: number | null | undefined,
): number | undefined {
  if (!model || (inputTokens == null && outputTokens == null)) return undefined;

  // Exact match first, then prefix match (longest key first)
  let multipliers = MODEL_COST_MULTIPLIERS[model];
  if (!multipliers) {
    const keys = Object.keys(MODEL_COST_MULTIPLIERS).sort(
      (a, b) => b.length - a.length,
    );
    for (const key of keys) {
      if (model.startsWith(key)) {
        multipliers = MODEL_COST_MULTIPLIERS[key];
        break;
      }
    }
  }
  if (!multipliers) return undefined;

  const inp = (inputTokens ?? 0) * multipliers.input;
  const out = (outputTokens ?? 0) * multipliers.output;
  return (inp + out) * 0.00001;
}
